import { ID_PART_PATTERN } from "./id-grammar.js";
import { z } from "zod";
//#region src/app-introspection-inputs.ts
/**
* App-facing REQUEST shapes for the five DP-introspection routes (apps-MCP v2 V-2,
* plus the app-database observation).
*
* ⛔ **Defined ONCE here and imported by BOTH** the site-admin-api controller
* (`@Query`/`@Param`/`@Body`) and the `app-introspection-tools.ts` rows
* (`argsSchema`). The tool rows must NOT re-declare them: two copies of a bound
* input is how the advertised `inputSchema` and the enforced route schema drift
* apart, and a tool that advertises a filter the endpoint rejects is a 400 the
* agent cannot diagnose — silent on both sides because each half is correct in
* isolation (R45-06).
*
* ⚠️ These are the APP-FACING shapes. The RESPONSE contract for the same five
* endpoints lives in `@zion/sdk-cp-dp-contracts` (`app-introspection.schemas.ts`),
* because it is a CP↔DP seam shape. Different direction, different package.
*/
/**
* A fixture id, in **exactly the DP's own domain**.
*
* ⛔ It is built from `ID_PART_PATTERN` + `MAX_FIXTURE_ID_LENGTH` — the same two
* constants `@zion/sdk-cp-dp-contracts`' `FixtureIdSchema` binds — so this door and
* the far one cannot disagree. ⛔ **Do NOT substitute this package's
* `FIXTURE_ID_PATTERN`** (a DNS-1123 label): measured by execution, it is WIDER in
* four ways (leading digit, consecutive hyphens, 25 chars, 63 chars) and narrower
* in none, so it would accept values the DP rejects. See `id-grammar.ts`.
*
* ⚠️ `.max()` precedes `.regex()` — the repo's ReDoS rule: bound the input before
* running the match.
*/
const appIntrospectionFixtureIdSchema = z.string().min(1).max(24).regex(ID_PART_PATTERN);
/**
* ⭐ EMPTY, and `.strict()` — which is the whole point. `appId`/`dpId` are PATH
* params, so the query object carries nothing; without a bound schema the
* controller's "rejected by `.strict()`" list would be **prose describing a control
* that does not exist**. An empty strict object makes every one of these a real
* 400: `namespace`, `labelSelector`, `orgId`, `url`, `host`, `port`, `path`,
* `stream`, `principalId`, `timeoutMs` — each of which would otherwise let a caller
* widen or aim a read the server derives.
*/
const getAppStreamInfoQuerySchema = z.object({}).strict();
const getAppDependenciesQuerySchema = z.object({}).strict();
/**
* ⭐ EMPTY and `.strict()`, for the same reason as its two siblings above, and NOT a
* reuse of one of them. `appId`/`dpId` are PATH params, so this route's query object
* carries nothing, and a bound empty strict schema is what makes `namespace`,
* `labelSelector`, `orgId`, `schema`, `table`, `since`, `window` and `limit` real 400s
* rather than keys zod 4 silently strips.
*
* ⛔ A separate declaration even though it is byte-identical to
* `getAppDependenciesQuerySchema` today. Sharing one symbol across two routes means a
* later optional narrowing on the other route widens THIS one silently; the three
* existing declarations in this file are identical for the same reason and are
* deliberately not deduplicated.
*
* ⚠️ `window` and `limit` are named in that rejection list on purpose. The window and
* the bucket size are contract literals (`AppDatabaseWindowSchema.bucketSeconds` is
* `z.literal(300)`), and the row cap is the server's `MAX_APP_DATABASE_TABLE_ROWS`. A
* caller that could set either would be able to narrow itself into a partial answer
* that reads like a complete one.
*/
const getAppDatabaseObservationQuerySchema = z.object({}).strict();
/** The one optional narrowing: a single fixture instead of all of the app's. */
const getAppConfigQuerySchema = z.object({ fixtureId: appIntrospectionFixtureIdSchema.optional() }).strict();
/**
* ⛔ The probe takes **NO body and NO query** — `{appId, dpId, fixtureId}` are all
* path params (V-2b / A1: nothing that could name a target may cross). A `.strict()`
* empty body schema is still bound, so a POST carrying `{"url": …}` is a 400 at the
* door rather than an ignored key.
*/
const probeAppFixtureBodySchema = z.object({}).strict();
//#endregion
export { appIntrospectionFixtureIdSchema, getAppConfigQuerySchema, getAppDatabaseObservationQuerySchema, getAppDependenciesQuerySchema, getAppStreamInfoQuerySchema, probeAppFixtureBodySchema };

//# sourceMappingURL=app-introspection-inputs.js.map